import { useState, type ChangeEvent, type FormEvent } from 'react';
import api, { ApiError } from '../../lib/apiClient';
import { useConfig } from '../../context/ConfigContext';
import { IconGoogle, IconMicrosoft } from '../../components/icons';

// TS port of the source (Falah) app's pages/system/Authentication.jsx, with
// the Singpass tab removed entirely — Singpass SSO was deleted from the
// backend in an earlier phase of this migration (SsoController only
// supports 'google' and 'microsoft'; see api/app/Http/Controllers/SsoController.php),
// and PUT /settings/sso only validates google_*/microsoft_* fields.

type Provider = 'google' | 'microsoft';

const PROVIDERS: { value: Provider; label: string; icon: typeof IconGoogle }[] = [
  { value: 'google', label: 'Google', icon: IconGoogle },
  { value: 'microsoft', label: 'Microsoft', icon: IconMicrosoft },
];

interface SsoForm {
  google_sso_enabled: boolean;
  google_client_id: string;
  google_client_secret: string;
  microsoft_sso_enabled: boolean;
  microsoft_client_id: string;
  microsoft_client_secret: string;
}

export function Authentication() {
  // Settings loads asynchronously — mounting the form before it resolves
  // would freeze `ssoForm`'s initial state on ConfigContext's stub defaults,
  // since useState's initializer only ever runs once. Deferring the form's
  // own mount until real data has arrived guarantees `settings` below is
  // the real thing (same fix as Settings.tsx's identical guard).
  const { loaded } = useConfig();
  if (!loaded) return null;

  return <AuthenticationFormPage />;
}

function AuthenticationFormPage() {
  const { settings, reload } = useConfig();
  const [provider, setProvider] = useState<Provider>('google');

  const [ssoForm, setSsoForm] = useState<SsoForm>({
    google_sso_enabled: settings.google_sso_enabled,
    google_client_id: settings.google_client_id ?? '',
    google_client_secret: '',
    microsoft_sso_enabled: settings.microsoft_sso_enabled,
    microsoft_client_id: settings.microsoft_client_id ?? '',
    microsoft_client_secret: '',
  });
  const [ssoSaving, setSsoSaving] = useState(false);
  const [ssoSaved, setSsoSaved] = useState(false);
  const [ssoErrors, setSsoErrors] = useState<Record<string, string[]>>({});

  const setSso = (key: keyof SsoForm) => (e: ChangeEvent<HTMLInputElement>) => {
    setSsoSaved(false);
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setSsoForm({ ...ssoForm, [key]: value });
  };

  const saveSso = async (e: FormEvent) => {
    e.preventDefault();
    setSsoSaving(true);
    setSsoErrors({});
    try {
      await api.put('/settings/sso', ssoForm);
      await reload();
      setSsoForm((f) => ({ ...f, google_client_secret: '', microsoft_client_secret: '' }));
      setSsoSaved(true);
    } catch (err) {
      setSsoErrors(err instanceof ApiError && err.errors ? err.errors : { general: ['Save failed.'] });
    } finally {
      setSsoSaving(false);
    }
  };

  const copyToClipboard = (text: string) => navigator.clipboard?.writeText(text);

  return (
    <>
      <header className="page-header">
        <h1>Authentication</h1>
      </header>
      <p className="muted">Configure how staff sign in to the portal.</p>

      <div className="card">
        <h2>Single Sign-On</h2>
        <p className="muted">
          Let staff sign in with an existing Google or Microsoft account instead of a password. Accounts are matched
          by email against an existing user in System &gt; Users — SSO never creates new accounts on its own.
        </p>

        <div className="toolbar">
          <select value={provider} onChange={(e) => setProvider(e.target.value as Provider)}>
            {PROVIDERS.map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </select>
        </div>

        <form onSubmit={saveSso} className="form-grid">
          {ssoErrors.general && <div className="alert alert-error span-2">{ssoErrors.general[0]}</div>}
          {ssoSaved && <div className="alert alert-success span-2">Single sign-on settings saved.</div>}

          {provider === 'google' && (
            <>
              <label className="span-2 checkbox-label">
                <input type="checkbox" checked={ssoForm.google_sso_enabled} onChange={setSso('google_sso_enabled')} />
                Enable Google sign-in
              </label>
              <label>
                Client ID
                <input
                  value={ssoForm.google_client_id}
                  onChange={setSso('google_client_id')}
                  maxLength={255}
                  required={ssoForm.google_sso_enabled}
                />
                {ssoErrors.google_client_id && <small className="error">{ssoErrors.google_client_id[0]}</small>}
                {ssoForm.google_sso_enabled && <small className="muted">Required to enable Google sign-in.</small>}
              </label>
              <label>
                Client secret
                <input
                  type="password"
                  value={ssoForm.google_client_secret}
                  onChange={setSso('google_client_secret')}
                  placeholder={settings.google_client_secret_set ? 'Already set — leave blank to keep' : 'Not set'}
                  maxLength={255}
                  required={ssoForm.google_sso_enabled && !settings.google_client_secret_set}
                />
                {ssoErrors.google_client_secret && <small className="error">{ssoErrors.google_client_secret[0]}</small>}
                {ssoForm.google_sso_enabled && !settings.google_client_secret_set && (
                  <small className="muted">Required to enable Google sign-in.</small>
                )}
              </label>
              <label className="span-2">
                Redirect URI
                <div className="input-with-button">
                  <input value={settings.google_redirect_uri} readOnly />
                  <button
                    type="button"
                    className="btn btn-ghost"
                    onClick={() => copyToClipboard(settings.google_redirect_uri)}
                  >
                    Copy
                  </button>
                </div>
                <small className="muted">Add this as an authorised redirect URI in the Google Cloud Console.</small>
              </label>
            </>
          )}

          {provider === 'microsoft' && (
            <>
              <label className="span-2 checkbox-label">
                <input
                  type="checkbox"
                  checked={ssoForm.microsoft_sso_enabled}
                  onChange={setSso('microsoft_sso_enabled')}
                />
                Enable Microsoft sign-in
              </label>
              <label>
                Client ID
                <input
                  value={ssoForm.microsoft_client_id}
                  onChange={setSso('microsoft_client_id')}
                  maxLength={255}
                  required={ssoForm.microsoft_sso_enabled}
                />
                {ssoErrors.microsoft_client_id && <small className="error">{ssoErrors.microsoft_client_id[0]}</small>}
                {ssoForm.microsoft_sso_enabled && (
                  <small className="muted">Required to enable Microsoft sign-in.</small>
                )}
              </label>
              <label>
                Client secret
                <input
                  type="password"
                  value={ssoForm.microsoft_client_secret}
                  onChange={setSso('microsoft_client_secret')}
                  placeholder={settings.microsoft_client_secret_set ? 'Already set — leave blank to keep' : 'Not set'}
                  maxLength={255}
                  required={ssoForm.microsoft_sso_enabled && !settings.microsoft_client_secret_set}
                />
                {ssoErrors.microsoft_client_secret && (
                  <small className="error">{ssoErrors.microsoft_client_secret[0]}</small>
                )}
                {ssoForm.microsoft_sso_enabled && !settings.microsoft_client_secret_set && (
                  <small className="muted">Required to enable Microsoft sign-in.</small>
                )}
              </label>
              <label className="span-2">
                Redirect URI
                <div className="input-with-button">
                  <input value={settings.microsoft_redirect_uri} readOnly />
                  <button
                    type="button"
                    className="btn btn-ghost"
                    onClick={() => copyToClipboard(settings.microsoft_redirect_uri)}
                  >
                    Copy
                  </button>
                </div>
                <small className="muted">Add this as a redirect URI in the Azure App registration.</small>
              </label>
            </>
          )}

          <div className="form-actions span-2">
            <button className="btn btn-primary" disabled={ssoSaving}>
              {ssoSaving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </>
  );
}
